import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

// Initial state
const initialState = {
  // User's progress across all enrollments
  progressData: [],
  loading: false,
  error: null,
  
  // Course-specific assignments
  courseAssignments: null,
  courseAssignmentsLoading: false,
  courseAssignmentsError: null,
  
  // Pending assignments (all courses)
  pendingAssignments: [],
  pendingLoading: false,
  pendingError: null,
  
  // Completed assignments
  completedAssignments: [],
  completedLoading: false,
  completedError: null,
  
  // Single assignment template details
  assignmentTemplate: null,
  templateLoading: false,
  templateError: null,
  
  // Submission result
  submissionResult: null,
  submitting: false,
  submitError: null,
};

// Fetch user's progress across all enrollments
export const fetchUserProgress = createAsyncThunk(
  "progress/fetchUserProgress",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/user/progress");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch assignments for a specific cohort or enrollment
export const fetchCourseAssignments = createAsyncThunk(
  "progress/fetchCourseAssignments",
  async (params, { rejectWithValue }) => {
    try {
      let query = "";
      if (typeof params === "object" && params !== null) {
        const { cohortId, enrollmentId } = params;
        query = cohortId ? `cohort_id=${cohortId}` : `enrollment_id=${enrollmentId}`;
      } else {
        query = `cohort_id=${params}`;
      }
      const response = await api.get(`/user/progress/assignments?${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch pending assignments (all courses)
export const fetchPendingAssignments = createAsyncThunk(
  "progress/fetchPendingAssignments",
  async (params, { rejectWithValue }) => {
    try {
      let query = "";
      if (typeof params === "object" && params !== null) {
        const { cohortId, enrollmentId } = params;
        query = cohortId ? `cohort_id=${cohortId}` : `enrollment_id=${enrollmentId}`;
      } else {
        query = `cohort_id=${params}`;
      }
      const response = await api.get(`/user/progress/assignments/pending?${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch completed assignments
export const fetchCompletedAssignments = createAsyncThunk(
  "progress/fetchCompletedAssignments",
  async (params, { rejectWithValue }) => {
    try {
      let query = "";
      if (typeof params === "object" && params !== null) {
        const { cohortId, enrollmentId } = params;
        query = cohortId ? `cohort_id=${cohortId}` : `enrollment_id=${enrollmentId}`;
      } else {
        query = `cohort_id=${params}`;
      }
      const response = await api.get(`/user/progress/assignments/completed?${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch single assignment template details
export const fetchAssignmentTemplate = createAsyncThunk(
  "progress/fetchAssignmentTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/user/assignments/template/${templateId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Submit an assignment
export const submitAssignment = createAsyncThunk(
  "progress/submitAssignment",
  async ({ templateId, cohortId, courseId, weekNumber, file, description }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      if (templateId) {
        formData.append("template_id", templateId);
        formData.append("assignment_id", templateId);
        formData.append("id", templateId);
      }
      if (cohortId) formData.append("cohort_id", cohortId);
      if (courseId) formData.append("course_id", courseId);
      if (weekNumber) formData.append("week_number", weekNumber);
      if (file) formData.append("file_paths", file);
      if (description) {
        formData.append("description", description);
      }

      const response = await api.post("/user/assignments/submit", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Clear submission result
export const clearSubmissionResult = createAsyncThunk(
  "progress/clearSubmissionResult",
  async () => {
    return null;
  }
);

const progressSlice = createSlice({
  name: "progress",
  initialState,
  reducers: {
    clearProgressData: (state) => {
      state.progressData = [];
      state.error = null;
    },
    clearCourseAssignments: (state) => {
      state.courseAssignments = null;
      state.courseAssignmentsError = null;
    },
    clearAssignmentTemplate: (state) => {
      state.assignmentTemplate = null;
      state.templateError = null;
    },
    clearSubmitError: (state) => {
      state.submitError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user progress
      .addCase(fetchUserProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.progressData = action.payload.data || [];
      })
      .addCase(fetchUserProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch course assignments
      .addCase(fetchCourseAssignments.pending, (state) => {
        state.courseAssignmentsLoading = true;
        state.courseAssignmentsError = null;
      })
      .addCase(fetchCourseAssignments.fulfilled, (state, action) => {
        state.courseAssignmentsLoading = false;
        state.courseAssignments = action.payload.data || null;
      })
      .addCase(fetchCourseAssignments.rejected, (state, action) => {
        state.courseAssignmentsLoading = false;
        state.courseAssignmentsError = action.payload;
      })
      
      // Fetch pending assignments
      .addCase(fetchPendingAssignments.pending, (state) => {
        state.pendingLoading = true;
        state.pendingError = null;
      })
      .addCase(fetchPendingAssignments.fulfilled, (state, action) => {
        state.pendingLoading = false;
        state.pendingAssignments = action.payload.data?.assignments || [];
      })
      .addCase(fetchPendingAssignments.rejected, (state, action) => {
        state.pendingLoading = false;
        state.pendingError = action.payload;
      })
      
      // Fetch completed assignments
      .addCase(fetchCompletedAssignments.pending, (state) => {
        state.completedLoading = true;
        state.completedError = null;
      })
      .addCase(fetchCompletedAssignments.fulfilled, (state, action) => {
        state.completedLoading = false;
        state.completedAssignments = action.payload.data?.assignments || [];
      })
      .addCase(fetchCompletedAssignments.rejected, (state, action) => {
        state.completedLoading = false;
        state.completedError = action.payload;
      })
      
      // Fetch assignment template
      .addCase(fetchAssignmentTemplate.pending, (state) => {
        state.templateLoading = true;
        state.templateError = null;
      })
      .addCase(fetchAssignmentTemplate.fulfilled, (state, action) => {
        state.templateLoading = false;
        state.assignmentTemplate = action.payload.data || null;
      })
      .addCase(fetchAssignmentTemplate.rejected, (state, action) => {
        state.templateLoading = false;
        state.templateError = action.payload;
      })
      
      // Submit assignment
      .addCase(submitAssignment.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submissionResult = null;
      })
      .addCase(submitAssignment.fulfilled, (state, action) => {
        state.submitting = false;
        state.submissionResult = action.payload.data || null;
      })
      .addCase(submitAssignment.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
      })
      
      // Clear submission result
      .addCase(clearSubmissionResult.fulfilled, (state) => {
        state.submissionResult = null;
      });
  },
});

export const {
  clearProgressData,
  clearCourseAssignments,
  clearAssignmentTemplate,
  clearSubmitError,
} = progressSlice.actions;

export default progressSlice.reducer;
